//#region Angular Imports

import { StateService } from '@uirouter/angularjs'

//#endregion

import { ColumnModel } from '../api/api.models'
import { Api, LocalDictionaryFactory } from './../api/api.module'

import { CommonHelper } from '../../helpers/common-helper'
import { EventsNames, TemplateHelper, LocalizationHelper, ConfigHelper } from '../../helpers'

import { DistributorService } from '../../services/distributor'
import { GridService } from '../../services/grid'
import { UserSessionService } from '../../services/user-session'

import { Enums, Activity } from '../../models/common'
import { OpDistributor } from '../../models/core'

import { BaseGridController, ScopeBaseGrid } from '../common/common.base.grid.controller'
import { BulkEditInitParams } from '../common/create-edit-popup/create-edit-popup.controller'
import { GridTypes, GridController } from '../common/grid/grid.controller'
import { ButtonsOptions } from '../common/grid/grid.controller'
import { ImportOptions } from '../common/import/import.directive'
import { IIMRConfirmationPopup, IMRConfirmationPopupOptions, IMRConfirmationPopupInitializedArgs } from '../common/confirmation-popup2'
import { SystemService } from '../../services/system'


interface DistributorsScope extends ScopeBaseGrid {

}

export class DistributorsController extends BaseGridController {

    //#region Consts

    private get DISTRIBUTOR_GRID_NAME() { return 'DistributorsGrid' }

    //#endregion


    //#region Members

    private grid: GridController<OpDistributor>

    //#endregion


    //#region Ctor

    public constructor(
        protected $scope: DistributorsScope,
        protected $state: StateService,
        $timeout: ng.ITimeoutService,
        protected api: Api,
        protected localDictionary: LocalDictionaryFactory,
        private distributorService: DistributorService,
        private gridService: GridService,
        protected userSessionService: UserSessionService,
        protected systemService: SystemService,
        referenctType: Enums.ReferenceType,
        private moduleName: Enums.SimaxModuleName
    ) {
        super($scope, $timeout, $state, userSessionService, systemService, api, localDictionary, CommonHelper.IdDistributor, referenctType)

        this.setEvents()
        this.setMainGridOptions()
        this.initializeConfirmationPopup()
    }

    //#endregion


    //#region Events

    public onAdd(): void {
        this.$scope.$broadcast(EventsNames.INITIALIZE_CREATE_EDIT_POPUP, this.distributorService.getCreateEditOptions())
    }

    public onConfirmDelete(): void {
        this.distributorService
            .deleteDistributor(this.grid.selectedItemId)
            .then(() => {
                this.onDeleted()
            })
    }

    public onDelete(): void {
        this.confirmationPopup.show()
    }

    public onDeleted(): void {
        this.dataGrid.refresh()
    }

    public onEdit(): void {
        if (this.dataGrid.getSelectedRowKeys().length == 1) {
            this.$scope.$broadcast(
                EventsNames.INITIALIZE_CREATE_EDIT_POPUP,
                this.distributorService.getCreateEditOptions(this.grid.selectedItemId)
            )
        }
        else {
            const columnCount = this.dataGrid.columnCount()
            const columns = []

            for (let i = 0; i < columnCount; i++) {
                columns.push(this.dataGrid.columnOption(i));
            }

            const gridData = angular.copy(this.dataGrid.getSelectedRowsData())

            const bulkEditInitParams: BulkEditInitParams = {
                moduleName: Enums.SimaxModuleName.Distributor,
                idReferenceType: this.referenceType,
                groupReferenceType: Enums.ReferenceType.IdDistributor,
                groupReferenceValue: this.userSession.getLoggedOperator().idDistributor,
                selectedGridRows: gridData,
                gridColumns: columns,
                section: null,
                columnsReorderingDisabled: this.userSession.getLoggedOperator().hasPermission(Activity.WEBSIMAX_COLUMN_REORDER_DISALLOWED, Enums.UIObjectProperties.DistributorModuleMainGrid, false),
                useBusinessObjects: true
            }

            this.$scope.$root.$broadcast(EventsNames.INITIALIZE_BULK_EDIT_POPUP, bulkEditInitParams)
        }
    }

    public onShowDetails(e: any): void {
        this.distributorService.openDetails(this.grid.selectedItemId, this.grid.pageItemsIds)
    }

    public onInitialized(e: any, grid: GridController<OpDistributor>): void {
        this.setGridId(e.tableId)

        this.dataGrid = e.component
        this.grid = grid
    }

    //#endregion


    //#region Confirmation Popup

    public confirmationPopupOptions: IMRConfirmationPopupOptions
    private confirmationPopup: IIMRConfirmationPopup

    private initializeConfirmationPopup(): void {
        this.confirmationPopupOptions = {
            title: LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__DELETING, this.moduleName),
            content: LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__CONFIRM_DELETE, this.moduleName),

            onInitialized: (e: IMRConfirmationPopupInitializedArgs) => {
                this.confirmationPopup = e.component
            },

            onConfirmed: () => {
                this.onConfirmDelete()
            }
        }
    }

    //#endregion


    //#region Setup

    private getContextMenuItems(isTouchScreen: boolean = false): any[] {
        const items = [];

        if (this.userSession.getLoggedOperator().hasPermission(Activity.DISTRIBUTOR_EDIT)) {
            items.push({
                beginGroup: true,
                template: TemplateHelper.getContextMenuItem(LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Edit), ConfigHelper.Icon.EDIT, this.userSessionService),

                onItemClick: (e: any) => this.onEdit(),
            })
        }

        return this.mergeWithDefaultItems(isTouchScreen, items)
    }

    public getGridButtonsOptions(): ButtonsOptions {
        return {
            enabled: true,

            addPermissions: this.userSession.getLoggedOperator().hasPermission(Activity.DISTRIBUTOR_ADD) ? "['" + Activity[Activity.DISTRIBUTOR_ADD] + "']" : null,
            deletePermissions: this.userSession.getLoggedOperator().hasPermission(Activity.DISTRIBUTOR_DEL) ? "['" + Activity[Activity.DISTRIBUTOR_DEL] + "']" : null,
            editPermissions: this.userSession.getLoggedOperator().hasPermission(Activity.DISTRIBUTOR_EDIT) ? "['" + Activity[Activity.DISTRIBUTOR_EDIT] + "']" : null,
            exportPermissions: null,
            importPermissions: this.userSession.getLoggedOperator().hasPermission(Activity.DISTRIBUTOR_IMPORT_OP) ? "['" + Activity[Activity.DISTRIBUTOR_IMPORT_OP] + "']" : null,
            saveColumnSettingsPermissions: this.userSession.getLoggedOperator().hasPermission(Activity.WEBSIMAX_MODULE_VIEW_COLUMN_DEFINITION_OP) ? "['" + Activity[Activity.WEBSIMAX_MODULE_VIEW_COLUMN_DEFINITION_OP] + "']" : null,
            showDetailsPermissions: this.userSession.getLoggedOperator().hasPermission(Activity.DISTRIBUTOR_LIST) ? "['" + Activity[Activity.DISTRIBUTOR_LIST] + "']" : null,

            onShowClick: (e: any) => this.onShowDetails(e),
            onAddClick: () => this.onAdd(),
            onEditClick: () => this.onEdit(),
            onDeleteClick: () => this.onDelete(),
        }
    }

    public getImportOptions(): ImportOptions {
        return {
            dictionaryMethodName: null,
            popupName: LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__IMPORT, this.moduleName),
            requestMethodType: null,
            referenceType: this.referenceType,
            moduleName: this.moduleName,
            columnsReorderingDisabled: this.userSession.getLoggedOperator().hasPermission(Activity.WEBSIMAX_MODULE_VIEW_COLUMN_DEFINITION_OP, Enums.UIObjectProperties.DistributorModuleMainGrid)
        }
    }

    public setEvents() {
        this.$scope.$on(EventsNames.DISTRIBUTOR_DELETED, (event: ng.IAngularEvent, ...args: any[]) => { this.onDeleted() })
        this.$scope.showDetails = (e) => { this.onShowDetails(e) }
        this.$scope.confirmDelete = () => { this.onConfirmDelete() }
    }

    public setMainGridOptions(): void {
        this.gridService
            .getColumnModelByCategory(
                [
                    Enums.SimaxDataTypeGroupCategory.WEBSIMAX__MODULE_NAME__GRID_PARAMS,
                    Enums.SimaxDataTypeGroupCategory.WEBSIMAX__MODULE_NAME__DEFAULT_GRID_PARAMS
                ],
                this.moduleName,
                {
                    groupReferenceType: Enums.ReferenceType.IdDistributor,
                    groupReferenceValue: this.userSession.getLoggedOperator().idDistributor
                }
            )
            .then((columns: ColumnModel[], defaultColumns: ColumnModel[]) => {
                this.$scope.mainGridOptions = {
                    addCustomFilters: true,
                    buttonsOptions: this.getGridButtonsOptions(),
                    columnsDictionary: columns,
                    dataSourceAdditionalFields: null,
                    defaultColumnsDictionary: defaultColumns,
                    idReferenceType: this.referenceType,
                    importOptions: this.getImportOptions(),
                    localStorageVarForSavedEntity: CommonHelper.localStorageVarForSavedEntity.distributorSaved,
                    name: this.DISTRIBUTOR_GRID_NAME,
                    preExecuteClearFilter: this.preExecuteGridClearFilter,
                    referenceId: this.referenceType,
                    type: GridTypes.MAIN,

                    getDataSourceMethod: this.userSession.getDataProvider().Distributor.getDistributorFilter,

                    allowColumnReordering: !this.userSession
                        .getLoggedOperator()
                        .hasPermission(Activity.WEBSIMAX_COLUMN_REORDER_DISALLOWED, Enums.UIObjectProperties.DistributorModuleMainGrid),

                    onInitialized: (e: any, grid: GridController<OpDistributor>) => this.onInitialized(e, grid),
                    onDataReady: data => { this.onDataReady(data) },
                    onRowDoubleClick: e => { this.onShowDetails(e) },
                    contextMenuItems: (isTouchScreen: boolean = false) => { return this.getContextMenuItems(isTouchScreen) },
                }
            })
    }

    //#endregion
}

